import { Box, Pagination } from '@mui/material';
import { Availability_Status_Enum, Base_Unit_Type_Enum, Measurement_Unit_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { FormatMeasurementUnit, FormatPackagingVolumePrice, formatPriceByBillingType, round } from '@kheops/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductFilterToolbar from '../../common/components/ProductFilterToolbar';
import { PackagingsByCompanyQuery, usePackagingsByCompanyLazyQuery } from '../../queries/__generated__/packagingsByCompany.generated';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { formatPackagingPriceIncludingDescriptor } from '../../common/utils/common.utils';
import { usePackagingsFromCustomPriceListLazyQuery } from '../../queries/__generated__/packagingsFromCustomPriceList.generated';
import i18n from '../../i18n';
import { useSubCatalogByIdLazyQuery } from '../../queries/__generated__/subCatalogById.generated';
import Sheet from '../../common/components/Sheet';
import DiscountImage from '../../assets/images/3d-icons/discount.png';
import PackagingPricesCard from './PackagingPricesCard';

export interface PackagingPricesDetailsProps {
  companyId: string;
  customPriceListId?: string | null;
  subCatalogId?: string | null;
  globalDiscount: number;
  open: boolean;
  onClose: () => void;
}

export interface PackagingFilters {
  gtin?: string;
  name?: string;
  productFamilies: Product_Sub_Family_Name_Enum[];
}

export type PackagingDetail = PackagingsByCompanyQuery['packaging'][number] & {
  primaryPriceDisplay?: string;
  secondaryPriceDisplay?: string;
  discount?: number;
  discountedPrimaryPriceDisplay?: string;
  discountedSecondaryPriceDisplay?: string;
};

const PAGE_SIZE = 24;

export default function PackagingPricesDetails({ companyId, customPriceListId, subCatalogId, globalDiscount, open, onClose }: PackagingPricesDetailsProps): React.JSX.Element {
  const { t } = useTranslation(['contracts', 'packaging', 'products']);
  const priceFormatter = useCurrencyFormat();

  const [getPackagingsFromCustomPriceListLazyQuery, { data: packagingsFromCustomPriceList }] = usePackagingsFromCustomPriceListLazyQuery();
  const [getPackagings, { data: packagings }] = usePackagingsByCompanyLazyQuery();
  const [getSubCatalog, { data: subCatalog }] = useSubCatalogByIdLazyQuery();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<PackagingFilters>({ productFamilies: [] });
  const cardListRef = useRef<HTMLElement>(null);

  const percentFormatter = new Intl.NumberFormat(i18n.resolvedLanguage, { style: 'percent', maximumFractionDigits: 2 });

  useEffect(() => {
    if (open) {
      /* only trigger query when Details dialog is opened to prevent sending multiple requests
      when a chat channel is opened */
      if (customPriceListId) {
        getPackagingsFromCustomPriceListLazyQuery({
          variables: {
            customPriceListId,
          },
        });
      } else {
        getPackagings({
          variables: {
            companyId,
            availability: { _eq: Availability_Status_Enum.Available },
            skuPackagingsToIgnore: [],
          },
        });
      }

      if (subCatalogId) {
        getSubCatalog({
          variables: {
            id: subCatalogId,
          },
        });
      }
    }
  }, [open]);

  useEffect(() => {
    setFilters({ productFamilies: [] });
  }, [open]);

  useEffect(() => {
    if (packagingsFromCustomPriceList?.custom_price_list_packaging) {
      getPackagings({
        variables: {
          companyId,
          availability: { _eq: Availability_Status_Enum.Available },
          skuPackagingsToIgnore: packagingsFromCustomPriceList.custom_price_list_packaging.map(
            ({ packaging }) => packaging!.sku,
          ),
        },
      });
    }
  }, [packagingsFromCustomPriceList]);

  const handleSearch = useCallback((query: string, productFamilies: Product_Sub_Family_Name_Enum[]) => {
    const isGtin = !Number.isNaN(Number(query)) && query.length === 13;

    setFilters({
      ...filters,
      gtin: isGtin ? query : undefined,
      name: !isGtin ? query.toLocaleLowerCase() : undefined,
      productFamilies,
    });
    setPage(0);
  }, [filters]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage - 1);
    cardListRef.current?.scrollTo(0, 0);
  }, []);

  const allPackagings = useMemo(() => {
    const discountedPackagings: PackagingDetail[] = packagingsFromCustomPriceList?.custom_price_list_packaging.map(
      ({ discount, packaging }) => ({
        discount,
        ...packaging!,
      }),
    ) || [];
    let availablePackagings = discountedPackagings.concat(packagings?.packaging || []);

    if (subCatalogId) {
      availablePackagings = (subCatalog?.sub_catalog_by_pk?.sub_catalog_packagings || [])
        .map(({ packaging_sku }) => {
          return availablePackagings.find(({ sku }) => sku === packaging_sku);
        }).filter((packaging) => !!packaging);
    }

    return availablePackagings.map((packaging) => {
      const adjustedPackaging: PackagingDetail = { ...packaging };
      const formattedSecondaryPrice = formatPriceByBillingType(priceFormatter.format(round(adjustedPackaging!.price)), adjustedPackaging!.base_unit.billing_type);
      let discountedPrice: number;

      // we leverage the mutual exclusitivity of custom price list and global discount
      adjustedPackaging.discount = adjustedPackaging.discount ?? globalDiscount;

      if (adjustedPackaging.discount) {
        discountedPrice = round(adjustedPackaging!.price * (1 - adjustedPackaging.discount));
        adjustedPackaging.secondaryPriceDisplay = formattedSecondaryPrice;
        adjustedPackaging.discountedSecondaryPriceDisplay = formatPackagingPriceIncludingDescriptor(
          t,
          formatPriceByBillingType(priceFormatter.format(discountedPrice), adjustedPackaging!.base_unit.billing_type),
          adjustedPackaging.trade_item_unit_descriptor,
        );
      } else {
        adjustedPackaging.secondaryPriceDisplay = formatPackagingPriceIncludingDescriptor(t, formattedSecondaryPrice, adjustedPackaging.trade_item_unit_descriptor);
      }

      if (adjustedPackaging?.base_unit.unit_type === Base_Unit_Type_Enum.Bulk) {
        const volumePrice = FormatPackagingVolumePrice(adjustedPackaging, adjustedPackaging.base_unit);
        const formattedPrimaryPrice = priceFormatter.format(round(volumePrice.value));

        adjustedPackaging.primaryPriceDisplay = `${formattedPrimaryPrice}/${FormatMeasurementUnit(volumePrice.unit as Measurement_Unit_Enum, 'fr', 1)}`;

        if (adjustedPackaging.discount) {
          const discountedVolumePrice = round(volumePrice.value * (1 - adjustedPackaging.discount));

          adjustedPackaging.secondaryPriceDisplay = formattedSecondaryPrice;
          adjustedPackaging.primaryPriceDisplay = formattedPrimaryPrice;
          adjustedPackaging.discountedPrimaryPriceDisplay = `${priceFormatter.format(discountedVolumePrice)}/${FormatMeasurementUnit(volumePrice.unit as Measurement_Unit_Enum, 'fr', 1)}`;
        }
      } else {
        adjustedPackaging.primaryPriceDisplay = formatPriceByBillingType(priceFormatter.format(round(adjustedPackaging!.price / adjustedPackaging!.unit_quantity!)), adjustedPackaging!.base_unit.billing_type);

        if (adjustedPackaging.discount) {
          adjustedPackaging.discountedPrimaryPriceDisplay = formatPriceByBillingType(priceFormatter.format(round((discountedPrice!) / adjustedPackaging!.unit_quantity!)), adjustedPackaging!.base_unit.billing_type);
        }
      }

      return adjustedPackaging;
    });
  }, [packagingsFromCustomPriceList, packagings, subCatalog]);

  const filteredPackagings = useMemo(() => {
    return allPackagings
      .filter((packaging) => {
        return (!filters.gtin || packaging.base_unit.gtin === filters.gtin)
          && (!filters.name || packaging.product.name.toLocaleLowerCase().includes(filters.name))
          && (!filters.productFamilies.length || filters.productFamilies.some((productFamily) => packaging.product.sub_family === productFamily));
      });
  }, [allPackagings, filters]);

  const currentPackagings = useMemo(() => {
    const pageStart = page * PAGE_SIZE;

    return filteredPackagings.slice(pageStart, pageStart + PAGE_SIZE);
  }, [filteredPackagings, page]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      width={368}
      title={t(
        packagingsFromCustomPriceList?.custom_price_list_packaging.length
          ? 'contracts:preferential_prices'
          : 'contracts:percent_of_discount',
        { percent: percentFormatter.format(globalDiscount) },
      )}
      titleIcon={<img src={DiscountImage} height={24} width={24} />}
      showCloseButton
    >
      <Box
        sx={{
          my: 3,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          '& .MuiInputBase-root': {
            width: '100%',
          },
        }}
      >
        <ProductFilterToolbar
          products={allPackagings.map(({ product }) => product)}
          onChange={handleSearch}
          shouldDisplayClearAllButton={false}
        />
        <Box
          ref={cardListRef}
          sx={{
            overflow: 'auto',
            mt: 2,
          }}
        >
          <Box>
            {currentPackagings.map((packaging) => (
              <PackagingPricesCard
                key={packaging.id}
                packaging={packaging}
              />
            ))}
          </Box>
          <Pagination
            color="primary"
            sx={{
              mt: 2,
              '& .MuiPagination-ul': {
                justifyContent: 'center',
              },
            }}
            page={page + 1}
            count={Math.ceil(filteredPackagings.length / PAGE_SIZE)}
            onChange={(_, newPage) => handlePageChange(newPage)}
          />
        </Box>
      </Box>
    </Sheet>
  );
}
